import { useMemo } from 'react';
import { Avatar, Icon, Span, Tooltip } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { getColorsForAvatar, getLettersForAvatar } from '~/utils/avatar';
import { getCurrentUser } from '~/common/explicit-exports';
import { TrailingButtonWrapper, UserInfoWrapper, UserListItemWrapper, UserLabel } from './UserListItem.styles';

type Props = {
  avatar?: string;
  showGenericAvatar?: boolean;
  isGroup?: boolean;
  name: string;
  label?: string;
  info?: string;
  onClick?: () => void;
  trailingButton?: JSX.Element;
  disabled?: boolean;
  href?: string;
};

export const UserListItem = ({
  avatar,
  showGenericAvatar,
  name,
  label,
  info,
  isGroup,
  onClick,
  trailingButton,
  href,
  disabled = false,
}: Props) => {
  const theme = useTheme();

  const {
    enterprise: { scheme_color: brandColor },
  } = getCurrentUser();

  const colors = getColorsForAvatar(theme, brandColor);

  const cursor = useMemo(() => {
    if (disabled) {
      return 'not-allowed';
    }

    if (onClick) {
      return 'pointer';
    }

    return 'auto';
  }, [disabled, onClick]);

  return (
    <UserListItemWrapper {...(onClick && { as: 'button', type: 'button', onClick, disabled })} $cursor={cursor}>
      <Avatar
        imgUrl={!showGenericAvatar && avatar}
        letters={!showGenericAvatar && !isGroup && getLettersForAvatar(name)}
        iconName={isGroup ? 'company:regular' : undefined}
        {...colors}
      />
      <UserInfoWrapper $isLink={!!href} {...(!!href && { as: 'a', href, target: '_blank', rel: 'noreferrer' })}>
        <Span>
          {name}{' '}
          {!!info && (
            <Tooltip message={info} placement="right">
              <Icon name="info:regular" color={theme.colors.secondary[400]} />
            </Tooltip>
          )}
        </Span>
        {!!label && <UserLabel size={0}>{label}</UserLabel>}
      </UserInfoWrapper>

      {trailingButton && <TrailingButtonWrapper>{trailingButton}</TrailingButtonWrapper>}
    </UserListItemWrapper>
  );
};
